import React, { useState, useMemo } from "react";
import { format, isToday, parseJSON } from "date-fns";
import { es } from "date-fns/locale";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionActions from "@material-ui/core/AccordionActions";
import Container from "@material-ui/core/Container";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Button from "@material-ui/core/Button";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EmailIcon from "@material-ui/icons/Email";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import RoomIcon from "@material-ui/icons/Room";
import ApartmentIcon from "@material-ui/icons/Apartment";
import { formatCelular } from "../functions";

const useStyles = makeStyles(theme => ({
  backButton: {
    marginRight: theme.spacing(2)
  },
  emailButton: {
    marginLeft: theme.spacing(2),
    "&:hover": {
      color: "inherit"
    }
  },
  whatsappButton: {
    marginLeft: theme.spacing(2),
    "&:hover": {
      color: "inherit"
    }
  },
  title: {
    flexGrow: 1
  },
  container: {
    height: "calc(100vh - 56px)",
    [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
      height: "calc(100vh - 48px)"
    },
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 64px)"
    },
    overflow: "auto"
  },
  loading: {
    minHeight: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  summary: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  nombre: {
    "&::before": {
      background: "none"
    }
  }
}));

const ClienteDetails = ({ mobile, cliente, onClose }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const { date, today } = useMemo(() => {
    setExpanded(false);

    let date = null;
    let today = null;

    if (cliente) {
      date = parseJSON(cliente.fecha_creacion_contacto);
      today = isToday(date);
    }

    return { date, today };
  }, [cliente]);

  const expandPanel = panel => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (!cliente) {
    return null;
  }

  const renderAddress = (direccion, direccion2) => {
    let addressComponent = null;
    let address2Component = null;

    if (direccion) {
      const addressJson = JSON.parse(direccion);

      if (addressJson) {
        let addressTitleText = "";

        if (addressJson.google) {
          addressTitleText =
            addressJson.addressName +
            " " +
            addressJson.addressNumber +
            ", " +
            addressJson.addressComuna;
        } else addressTitleText = addressJson.description;

        addressComponent = (
          <Accordion expanded={expanded === "direccion"} onChange={expandPanel("direccion")}>
            <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
              <ListItemIcon>
                <RoomIcon />
              </ListItemIcon>
              <ListItemText primary={expanded === "direccion" ? "Dirección" : addressTitleText} />
            </AccordionSummary>

            <AccordionDetails>
              <Container maxWidth="xs">
                <TextField fullWidth variant="standard" value={addressTitleText} />
              </Container>
            </AccordionDetails>

            <AccordionActions>
              <Button size="small" onClick={expandPanel(null)}>
                Cancelar
              </Button>
              <Button size="small" color="primary">
                Aceptar
              </Button>
            </AccordionActions>
          </Accordion>
        );
      }
    }

    if (direccion2) {
      address2Component = (
        <Accordion expanded={expanded === "direccion2"} onChange={expandPanel("direccion2")}>
          <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
            <ListItemIcon>
              <ApartmentIcon />
            </ListItemIcon>
            <ListItemText
              primary={expanded === "direccion2" ? "Edificio / Bloque / Departamento" : direccion2}
            />
          </AccordionSummary>

          <AccordionDetails>
            <Container maxWidth="xs">
              <TextField fullWidth variant="standard" value={direccion2} />
            </Container>
          </AccordionDetails>

          <AccordionActions>
            <Button size="small" onClick={expandPanel(null)}>
              Cancelar
            </Button>
            <Button size="small" color="primary">
              Aceptar
            </Button>
          </AccordionActions>
        </Accordion>
      );
    }

    return (
      <>
        {addressComponent}
        {address2Component}
      </>
    );
  };

  return (
    <div>
      <AppBar position="sticky">
        <Toolbar>
          {onClose && (
            <IconButton
              className={classes.backButton}
              edge="start"
              color="inherit"
              onClick={onClose}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Typography className={classes.title} variant="h6">
            {mobile && "Cliente"}
          </Typography>

          <Link
            className={classes.emailButton}
            href={`mailto:${cliente.body_contacto.email}`}
            color="inherit"
          >
            <IconButton edge="end" color="inherit">
              <EmailIcon />
            </IconButton>
          </Link>

          <Link
            className={classes.whatsappButton}
            href={`https://api.whatsapp.com/send?phone=569${cliente.body_contacto.telefono}`}
            target="_blank"
            rel="noopener noreferrer"
            color="inherit"
          >
            <IconButton edge="end" color="inherit">
              <WhatsAppIcon />
            </IconButton>
          </Link>
        </Toolbar>
      </AppBar>

      <Container disableGutters maxWidth="sm" className={classes.container}>
        <Paper square>
          <Typography className="p-2" component="p" variant="caption" align="right">
            {`Creado ${today ? "hoy," : "el"} ${format(date, today ? "p" : "dd MMM, p", {
              locale: es
            })}`}
          </Typography>

          <Accordion
            className={classes.nombre}
            expanded={expanded === "nombre"}
            onChange={expandPanel("nombre")}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {expanded !== "nombre" && (
                <Typography variant="h5">{cliente.nombre_contacto}</Typography>
              )}
            </AccordionSummary>

            <AccordionDetails>
              <Container maxWidth="xs">
                <Typography>{cliente.nombre_contacto}</Typography>
              </Container>
            </AccordionDetails>

            <AccordionActions>
              <Button size="small" onClick={expandPanel(null)}>
                Cancelar
              </Button>
              <Button size="small" color="primary">
                Aceptar
              </Button>
            </AccordionActions>
          </Accordion>

          {/* TODO}
          <Accordion expanded={expanded === "nota"} onChange={expandPanel("nota")}>
            <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
              <ListItemIcon>
                <NoteAddIcon />
              </ListItemIcon>
              <ListItemText primary={expanded === "nota" ? "Nota" : "Agregar nota"} />
            </AccordionSummary>

            <AccordionDetails>
              <Container maxWidth="xs">
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Agregar nota"
                  color="primary"
                  variant="standard"
                />
              </Container>
            </AccordionDetails>

            <AccordionActions>
              <Button size="small" onClick={expandPanel(null)}>
                Cancelar
              </Button>
              <Button size="small" color="primary">
                Aceptar
              </Button>
            </AccordionActions>
          </Accordion>
          {*/}

          <Accordion expanded={expanded === "telefono"} onChange={expandPanel("telefono")}>
            <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
              <ListItemIcon>
                <WhatsAppIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  expanded === "telefono"
                    ? "Teléfono"
                    : formatCelular(cliente.body_contacto.telefono)
                }
              />
            </AccordionSummary>

            <AccordionDetails>
              <Container maxWidth="xs">
                <TextField
                  fullWidth
                  placeholder="Agregar teléfono"
                  color="primary"
                  variant="standard"
                  value={formatCelular(cliente.body_contacto.telefono)}
                />
              </Container>
            </AccordionDetails>

            <AccordionActions>
              <Button size="small" onClick={expandPanel(null)}>
                Cancelar
              </Button>
              <Button size="small" color="primary">
                Aceptar
              </Button>
            </AccordionActions>
          </Accordion>

          <Accordion expanded={expanded === "email"} onChange={expandPanel("email")}>
            <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
              <ListItemIcon>
                <EmailIcon />
              </ListItemIcon>
              <ListItemText
                primary={expanded === "email" ? "Email" : cliente.body_contacto.email}
              />
            </AccordionSummary>

            <AccordionDetails>
              <Container maxWidth="xs">
                <TextField
                  fullWidth
                  placeholder="Agregar email"
                  color="primary"
                  variant="standard"
                  value={cliente.body_contacto.email}
                />
              </Container>
            </AccordionDetails>

            <AccordionActions>
              <Button size="small" onClick={expandPanel(null)}>
                Cancelar
              </Button>
              <Button size="small" color="primary">
                Aceptar
              </Button>
            </AccordionActions>
          </Accordion>

          {renderAddress(cliente.body_contacto.direccion, cliente.body_contacto.direccion2)}
        </Paper>
      </Container>
    </div>
  );
};

export default ClienteDetails;
